import { Button } from "@components/ui/button";
import { endpoint } from "@services/api.services";
import { useQueryClient } from "@tanstack/react-query";
import { showToast } from "@utils/helper/index.helper";
import { usePathname } from "@utils/hook/index.hook";
import { useBulkPatchData } from "@utils/query/index.query";
import { useSelectionStore } from "@utils/states/index.state";

/**
 * Bulk on/off switch for "Would you like to include a price?" (`is_price`).
 * Turning it off only hides the fee on the website -- the stored amount,
 * registration fee and discount are kept so turning it back on restores them.
 */
const BulkPriceToggle = ({ refetch }: { refetch?: () => void }) => {
  const pathname = usePathname();
  const queryClient = useQueryClient();
  const { selectedRows, clearSelection } = useSelectionStore();
  const { mutate, isPending } = useBulkPatchData();

  const handleToggle = (is_price: boolean) => {
    mutate(
      {
        data: { ids: selectedRows, is_price },
        endpoint: endpoint["course-bulk-price"],
      },
      {
        onSuccess: (response: any) => {
          const updated = response?.data?.updated ?? selectedRows.length;
          queryClient.invalidateQueries({
            queryKey: [`${pathname}-listing`],
          });
          queryClient.invalidateQueries({
            queryKey: [`${pathname}-listing-scroll`],
          });
          queryClient.invalidateQueries({ queryKey: ["course-section-listing"] });
          refetch?.();
          clearSelection();
          showToast({
            description: `Price ${
              is_price ? "shown on" : "hidden from"
            } ${updated} course${updated === 1 ? "" : "s"}.`,
            type: "success",
            title: "Success",
          });
        },
        onError: (error: any) => {
          const data = error?.response?.data || error?.data || {};
          const message =
            data?.error ||
            Object.values(data)[0] ||
            "Failed to update the price setting";
          showToast({
            description: message as string,
            type: "error",
            title: "Error",
          });
        },
      }
    );
  };

  return (
    <>
      <Button
        variant={"defaultoutline"}
        disabled={isPending || selectedRows.length === 0}
        onClick={() => handleToggle(true)}
      >
        Show Price {`(${selectedRows.length})`}
      </Button>
      <Button
        variant={"outline"}
        disabled={isPending || selectedRows.length === 0}
        onClick={() => handleToggle(false)}
      >
        Hide Price {`(${selectedRows.length})`}
      </Button>
    </>
  );
};

export default BulkPriceToggle;
